/**
 * `window.showCookiePreferences`: reopens the consent banner's preferences modal
 * from anywhere on the page — the footer's "Cookie settings" button uses it, so
 * that withdrawing consent is as easy as giving it (GDPR Art. 7(3)).
 *
 * vanilla-cookieconsent builds that modal lazily, and only once `run()` has
 * loaded its translations. `run()` bails out before that for bot user agents
 * and for WebDriver-driven browsers (`hideFromBots`, on by default), and its
 * `showPreferences()` then throws a TypeError from inside the library. Wrapped
 * here so that a click in such a browser is a logged no-op, not an uncaught
 * exception from a click handler. Measured against vanilla-cookieconsent 3.1.0
 * in tests/consent/show-preferences.unit.spec.ts.
 */

export interface PreferencesBanner {
  showPreferences: () => void;
}

export type Warn = (message: string, cause: unknown) => void;

const warnInConsole: Warn = (message, cause) => {
  // eslint-disable-next-line no-console
  console.warn(message, cause);
};

export const createShowCookiePreferences =
  (banner: PreferencesBanner, warn: Warn = warnInConsole) =>
  (): void => {
    try {
      banner.showPreferences();
    } catch (cause) {
      warn(
        'Cookie preferences cannot be shown: the consent banner did not initialise in this browser.',
        cause,
      );
    }
  };
